import React, { useState, useRef, useEffect } from 'react';
import { 
  IonContent, 
  IonPage, 
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon
} from '@ionic/react';
import { keyOutline, lockClosedOutline, homeOutline, sparklesOutline, menuOutline, closeOutline } from 'ionicons/icons';
import { motion, useInView, useAnimation } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import SuccessModal from '../components/SuccessModal';
import './Home.css';

const Home: React.FC = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interest: ''
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    interest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [navbarVisible, setNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [appNotifyEmail, setAppNotifyEmail] = useState('');
  const [appNotifyError, setAppNotifyError] = useState('');
  const [isSubmittingNotify, setIsSubmittingNotify] = useState(false);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  const validateName = (name: string): string => {
    const trimmed = name?.trim() || '';
    if (!trimmed) {
      return 'Name is required';
    }
    if (trimmed.length < 2) {
      return 'Name must be at least 2 characters';
    }
    if (!/^[a-zA-Z\s'-]+$/.test(trimmed)) {
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return '';
  };
  
  const validateEmail = (email: string): string => {
    const trimmed = email?.trim() || '';
    if (!trimmed) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      return 'Please enter a valid email address';
    }
    return '';
  };
  
  const validateInterest = (interest: string): string => {
    if (!interest || interest.trim() === '') {
      return 'Please select your interest';
    }
    return '';
  };
  
  const heroRef = useRef<HTMLDivElement>(null);
  const valuePropsRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const appComingSoonRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  
  const heroInView = useInView(heroRef, { once: true, margin: "-100px" });
  const valuePropsInView = useInView(valuePropsRef, { once: true, margin: "-100px" });
  const aboutInView = useInView(aboutRef, { once: true, margin: "-100px" });
  const appComingSoonInView = useInView(appComingSoonRef, { once: true, margin: "-100px" });
  const formInView = useInView(formRef, { once: true, margin: "-100px" });
  
  const heroControls = useAnimation();
  const valuePropsControls = useAnimation();
  const aboutControls = useAnimation();
  const appComingSoonControls = useAnimation();
  const formControls = useAnimation();
  
  useEffect(() => {
    if (heroInView) heroControls.start("visible");
    if (valuePropsInView) valuePropsControls.start("visible");
    if (aboutInView) aboutControls.start("visible");
    if (appComingSoonInView) appComingSoonControls.start("visible");
    if (formInView) formControls.start("visible");
  }, [heroInView, valuePropsInView, aboutInView, appComingSoonInView, formInView]);

  // Countdown timer - Set launch date to 3 months from now
  useEffect(() => {
    const launchDate = new Date();
    launchDate.setMonth(launchDate.getMonth() + 3);
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = launchDate.getTime() - now;
      
      if (distance > 0) {
        setCountdown({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 100) {
        setNavbarVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setNavbarVisible(false);
      } else {
        setNavbarVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  
  const scrollToForm = () => {
    const formElement = document.getElementById('lead-form');
    formElement?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAppNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const emailError = validateEmail(appNotifyEmail);
    if (emailError) {
      setAppNotifyError(emailError);
      return;
    }
    
    setIsSubmittingNotify(true);
    setAppNotifyError('');
    
    try {
      // eslint-disable-next-line no-console
      console.log('Adding app notification to Firestore...');
      const docRef = await addDoc(collection(db, 'app_notifications'), {
        email: appNotifyEmail.trim().toLowerCase(),
        timestamp: serverTimestamp(),
        type: 'app_launch_notification',
        metadata: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform
        }
      });
      
      // eslint-disable-next-line no-console
      console.log('✅ App notification submitted successfully! Document ID:', docRef.id);
      
      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setAppNotifyEmail('');
      setShowSuccessModal(true);
      
      // eslint-disable-next-line no-console
      console.log('✅ Success modal should be visible now!');
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('❌ Error submitting app notification:', error);
      // eslint-disable-next-line no-console
      console.error('Error details:', {
        message: error?.message,
        code: error?.code,
        stack: error?.stack
      });
      
      setAppNotifyError(error?.message || 'Failed to subscribe. Please check your connection and try again.');
    } finally {
      setIsSubmittingNotify(false);
      // eslint-disable-next-line no-console
      console.log('App notification submission finished, isSubmittingNotify set to false');
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // eslint-disable-next-line no-console
    console.log('=== FORM SUBMIT TRIGGERED ===');
    // eslint-disable-next-line no-console
    console.log('Form data:', formData);
    
    // Validate all fields
    const nameError = validateName(formData.name);
    const emailError = validateEmail(formData.email);
    const interestError = validateInterest(formData.interest);
    
    const errors = {
      name: nameError,
      email: emailError,
      interest: interestError
    };
    
    // eslint-disable-next-line no-console
    console.log('Validation errors:', errors);
    
    setFormErrors(errors);
    
    if (nameError || emailError || interestError) {
      // eslint-disable-next-line no-console
      console.log('Validation failed, stopping submission');
      // Scroll to first error
      setTimeout(() => {
        const firstError = document.querySelector('.form-item-error');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    
    // eslint-disable-next-line no-console
    console.log('Validation passed, starting submission...');
    setIsSubmitting(true);
    
    try {
      // eslint-disable-next-line no-console
      console.log('Adding document to Firestore...');
      const docRef = await addDoc(collection(db, 'leads'), {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        interest: formData.interest,
        timestamp: serverTimestamp(),
        metadata: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform
        }
      });
      
      // eslint-disable-next-line no-console
      console.log('✅ Form submitted successfully! Document ID:', docRef.id);
      
      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // eslint-disable-next-line no-console
      console.log('Setting showSuccessModal to true...');
      setShowSuccessModal(true);
      
      // eslint-disable-next-line no-console
      console.log('showSuccessModal state set to:', true);
      
      setFormData({ name: '', email: '', interest: '' });
      setFormErrors({ name: '', email: '', interest: '' });
      
      // eslint-disable-next-line no-console
      console.log('✅ Success modal should be visible now!');
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('❌ Error submitting form:', error);
      // eslint-disable-next-line no-console
      console.error('Error details:', {
        message: error?.message,
        code: error?.code,
        stack: error?.stack
      });
      
      setFormErrors({
        name: '',
        email: error?.message || 'Failed to submit. Please check your connection and try again.',
        interest: ''
      });
    } finally {
      setIsSubmitting(false);
      // eslint-disable-next-line no-console
      console.log('Form submission finished, isSubmitting set to false');
    }
  };
  
  const handleNameChange = (value: string) => {
    const newValue = value || '';
    // Use functional update to avoid stale closures
    setFormData(prev => {
      const updated = { ...prev, name: newValue };
      // Only validate if there was a previous error
      if (formErrors.name) {
        setFormErrors(prevErrors => ({ ...prevErrors, name: validateName(newValue) }));
      }
      return updated;
    });
  };
  
  const handleEmailChange = (value: string) => {
    const newValue = value || '';
    // Use functional update to avoid stale closures
    setFormData(prev => {
      const updated = { ...prev, email: newValue };
      // Only validate if there was a previous error
      if (formErrors.email) {
        setFormErrors(prevErrors => ({ ...prevErrors, email: validateEmail(newValue) }));
      }
      return updated;
    });
  };
  
  const handleInterestChange = (value: string) => {
    const newValue = value || '';
    // Use functional update to avoid stale closures
    setFormData(prev => {
      const updated = { ...prev, interest: newValue };
      // Only validate if there was a previous error
      if (formErrors.interest) {
        setFormErrors(prevErrors => ({ ...prevErrors, interest: validateInterest(newValue) }));
      }
      return updated;
    });
  };
  
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.42, 0, 0.58, 1] as const }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.42, 0, 0.58, 1] as const }
    }
  };
  
  const floatingKeyVariants = {
    float: {
      y: [0, -20, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: [0.42, 0, 0.58, 1] as const
      }
    }
  };
  
  return (
    <IonPage>
      <IonContent fullscreen className="luxury-content" scrollY={true}>
        {/* Navbar */}
        <motion.nav 
          className={`luxury-navbar ${navbarVisible ? 'navbar-visible' : 'navbar-hidden'}`}
          initial={{ y: -100, opacity: 0 }}
          animate={{ 
            y: navbarVisible ? 0 : -100, 
            opacity: navbarVisible ? 1 : 0 
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <div className="navbar-container">
            <div className="navbar-left">
              <div className="navbar-logo">
                <img src="/assets/icon/iconblack.png" alt="KeyToDXB Logo" className="logo-image" />
              </div>
              <div className="navbar-brand">
                <span className="brand-name">KeyToDXB</span>
                <span className="brand-tagline">Dubai Golden Visa</span>
              </div>
            </div>
            
            <div className="navbar-right desktop-only">
              <div className="navbar-social">
                <a 
                  href="https://www.instagram.com/keytodxb" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-link"
                  aria-label="Instagram"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162 0 3.403 2.759 6.162 6.162 6.162 3.403 0 6.162-2.759 6.162-6.162 0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4 2.209 0 4 1.791 4 4 0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="currentColor"/>
                  </svg>
                </a>
                <a 
                  href="https://www.linkedin.com/company/keytodxb" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-link"
                  aria-label="LinkedIn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor"/>
                  </svg>
                </a>
              </div>
              <IonButton 
                className="join-waitlist-btn"
                onClick={scrollToForm}
              >
                Join Waitlist
              </IonButton>
            </div>
            
            <button 
              className="mobile-menu-toggle mobile-only"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <IonIcon icon={isMenuOpen ? closeOutline : menuOutline} />
            </button>
          </div>
          
          {/* Mobile Side Menu */}
          <motion.div
            className={`mobile-side-menu ${isMenuOpen ? 'menu-open' : ''}`}
            initial={false}
            animate={{
              x: isMenuOpen ? 0 : '-100%'
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="side-menu-content">
              <div className="side-menu-header">
                <div className="side-menu-logo">
                  <img src="/assets/icon/iconblack.png" alt="KeyToDXB Logo" className="logo-image" />
                </div>
                <h3 className="side-menu-brand">KeyToDXB</h3>
                <p className="side-menu-tagline">Dubai Golden Visa</p>
              </div>
              
              <div className="side-menu-items">
                <button 
                  className="side-menu-item"
                  onClick={() => {
                    scrollToForm();
                    setIsMenuOpen(false);
                  }}
                >
                  Join Waitlist
                </button>
              </div>
              
              <div className="side-menu-social">
                <a 
                  href="https://www.instagram.com/keytodxb" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="side-menu-social-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162 0 3.403 2.759 6.162 6.162 6.162 3.403 0 6.162-2.759 6.162-6.162 0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4 2.209 0 4 1.791 4 4 0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="currentColor"/>
                  </svg>
                  Instagram
                </a>
                <a 
                  href="https://www.linkedin.com/company/keytodxb" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="side-menu-social-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor"/>
                  </svg>
                  LinkedIn
                </a>
              </div>
            </div>
          </motion.div>
          
          {/* Overlay */}
          {isMenuOpen && (
            <motion.div 
              className="side-menu-overlay"
              onClick={() => setIsMenuOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            ></motion.div>
          )}
        </motion.nav>
        
        {/* Hero Section */}
        <motion.section 
          ref={heroRef}
          className="hero-section"
          initial="hidden"
          animate={heroControls}
          variants={containerVariants}
        >
          <div className="hero-background">
            {/* Video Background - Hidden on mobile, shown on desktop */}
            <video 
              className="hero-video desktop-video"
              autoPlay
              loop
              muted
              playsInline
              poster="https://images.pexels.com/photos/15203321/pexels-photo-15203321.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            >
              <source src="/assets/video/timelapse.mp4" type="video/mp4" />
            </video>
            {/* Mobile Image Background */}
            <img 
              src="https://images.pexels.com/photos/15203321/pexels-photo-15203321.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop" 
              alt="Dubai Skyline" 
              className="hero-mobile-image"
            />
            {/* Fallback image if video doesn't load */}
            <img 
              src="https://images.pexels.com/photos/15203321/pexels-photo-15203321.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop" 
              alt="Dubai Skyline" 
              className="hero-fallback-image"
            />
            <div className="hero-overlay"></div>
            <div className="hero-gradient"></div>
          </div>
          
          <div className="hero-content">
            <motion.div
              variants={floatingKeyVariants}
              animate="float"
              className="floating-key"
            >
              <IonIcon icon={keyOutline} />
            </motion.div>
            
            <motion.h1 
              className="hero-headline"
              variants={containerVariants}
            >
              Unlock Your Future in Dubai
            </motion.h1>
            
            <motion.p 
              className="hero-subheadline"
              variants={containerVariants}
            >
              The smartest way to navigate Golden Visas and Luxury Real Estate.
              <br />
              The key to the city is coming soon.
            </motion.p>
            
            <motion.div variants={containerVariants}>
              <IonButton 
                className="priority-access-btn"
                onClick={scrollToForm}
                size="large"
              >
                Priority Access
              </IonButton>
            </motion.div>
          </div>
        </motion.section>
        
        {/* Value Props Section */}
        <motion.section 
          ref={valuePropsRef}
          className="value-props-section"
          initial="hidden"
          animate={valuePropsControls}
          variants={containerVariants}
        >
          <div className="section-container">
            <h2 className="section-title">The Gateway to Dubai Luxury</h2>
            
            <div className="value-cards-grid">
              <motion.div variants={containerVariants}>
                <IonCard className="value-card value-card-golden-visa">
                  <div className="value-card-image">
                    <img 
                      src="https://images.pexels.com/photos/15203321/pexels-photo-15203321.jpeg?auto=compress&cs=tinysrgb&w=1200&h=800&fit=crop" 
                      alt="Dubai City Buildings" 
                      className="value-card-bg"
                    />
                    <div className="value-card-overlay"></div>
                  </div>
                  <IonCardHeader>
                    <IonIcon icon={sparklesOutline} className="value-icon" />
                    <IonCardTitle>Golden Visa Eligibility</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    Check your status in 60 seconds. Fast-track your path to Dubai residency.
                  </IonCardContent>
                </IonCard>
              </motion.div>
              
              <motion.div variants={containerVariants}>
                <IonCard className="value-card value-card-property">
                  <div className="value-card-image">
                    <img 
                      src="https://images.pexels.com/photos/12909940/pexels-photo-12909940.jpeg?auto=compress&cs=tinysrgb&w=1200&h=800&fit=crop" 
                      alt="Dubai Skyline Sunset" 
                      className="value-card-bg"
                    />
                    <div className="value-card-overlay"></div>
                  </div>
                  <IonCardHeader>
                    <IonIcon icon={homeOutline} className="value-icon" />
                    <IonCardTitle>Off-Market Access</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    Exclusive properties before they hit the market. First access to premium listings.
                  </IonCardContent>
                </IonCard>
              </motion.div>
              
              <motion.div variants={containerVariants}>
                <IonCard className="value-card value-card-concierge">
                  <div className="value-card-image">
                    <img 
                      src="https://images.pexels.com/photos/31534657/pexels-photo-31534657.jpeg?auto=compress&cs=tinysrgb&w=1200&h=800&fit=crop" 
                      alt="Modern Downtown Dubai Skyscrapers" 
                      className="value-card-bg"
                    />
                    <div className="value-card-overlay"></div>
                  </div>
                  <IonCardHeader>
                    <IonIcon icon={lockClosedOutline} className="value-icon" />
                    <IonCardTitle>Concierge Service</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    Professional guidance for your Dubai journey. Your relocation, simplified.
                  </IonCardContent>
                </IonCard>
              </motion.div>
            </div>
          </div>
        </motion.section>
        
        {/* About Us Section */}
        <motion.section 
          ref={aboutRef}
          className="about-section"
          initial="hidden"
          animate={aboutControls}
          variants={containerVariants}
        >
          <div className="section-container">
            <div className="about-content">
              <div className="about-header">
                <h2 className="section-title">About KeyToDXB</h2>
                <div className="about-image-container">
                  <img 
                    src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                    alt="Dubai Skyline" 
                    className="about-image"
                  />
                </div>
              </div>
              <div className="about-text-container">
                <p className="about-text">
                  KeyToDXB bridges the gap between investors and Dubai&apos;s elite opportunities 
                  through cutting-edge technology. We combine luxury real estate expertise 
                  with innovative digital solutions to make your Dubai dream accessible.
                </p>
                <p className="about-text">
                  Whether you&apos;re seeking a Golden Visa, investing in premium property, 
                  or planning your relocation, we provide the tools and guidance you need 
                  to unlock your future in one of the world&apos;s most dynamic cities.
                </p>
              </div>
            </div>
          </div>
        </motion.section>
        
        {/* Lead Gen Form */}
        <motion.section 
          ref={formRef}
          id="lead-form"
          className="form-section"
          initial="hidden"
          animate={formControls}
          variants={containerVariants}
        >
          <div className="section-container">
            <IonCard className="form-card">
              <IonCardHeader>
                <IonCardTitle className="form-title">Join the Waitlist</IonCardTitle>
                <p className="form-subtitle">Be among the first to unlock Dubai&apos;s opportunities</p>
              </IonCardHeader>
              <IonCardContent>
                <form onSubmit={handleSubmit} className="lead-form">
                  <div className="form-field-wrapper">
                    <IonItem className={`form-item ${formErrors.name ? 'form-item-error' : ''}`}>
                      <IonLabel position="stacked">Full Name</IonLabel>
                      <IonInput
                        type="text"
                        value={formData.name}
                        onIonInput={(e) => {
                          try {
                            const value = e.detail.value || '';
                            handleNameChange(value);
                          } catch (err) {
                            // Silently handle any Ionic internal errors
                          }
                        }}
                        onBlur={() => {
                          setFormErrors(prev => ({ ...prev, name: validateName(formData.name) }));
                        }}
                        placeholder="Enter your full name"
                        required
                      />
                    </IonItem>
                    {formErrors.name && <span className="form-error">{formErrors.name}</span>}
                  </div>
                  
                  <div className="form-field-wrapper">
                    <IonItem className={`form-item ${formErrors.email ? 'form-item-error' : ''}`}>
                      <IonLabel position="stacked">Email Address</IonLabel>
                      <IonInput
                        type="email"
                        value={formData.email}
                        onIonInput={(e) => {
                          try {
                            const value = e.detail.value || '';
                            handleEmailChange(value);
                          } catch (err) {
                            // Silently handle any Ionic internal errors
                          }
                        }}
                        onBlur={() => {
                          setFormErrors(prev => ({ ...prev, email: validateEmail(formData.email) }));
                        }}
                        placeholder="your.email@example.com"
                        required
                      />
                    </IonItem>
                    {formErrors.email && <span className="form-error">{formErrors.email}</span>}
                  </div>
                  
                  <div className="form-field-wrapper">
                    <IonItem className={`form-item ${formErrors.interest ? 'form-item-error' : ''}`}>
                      <IonLabel position="stacked">Primary Interest</IonLabel>
                      <IonSelect
                        value={formData.interest}
                        placeholder="Select your interest"
                        onIonChange={(e) => {
                          const value = e.detail.value as string || '';
                          handleInterestChange(value);
                          setFormErrors(prev => ({ ...prev, interest: validateInterest(value) }));
                        }}
                        interface="popover"
                      >
                        <IonSelectOption value="golden-visa">Golden Visa</IonSelectOption>
                        <IonSelectOption value="property-investment">Property Investment</IonSelectOption>
                        <IonSelectOption value="relocation">Relocation</IonSelectOption>
                      </IonSelect>
                    </IonItem>
                    {formErrors.interest && <span className="form-error">{formErrors.interest}</span>}
                  </div>
                  
                  <IonButton
                    type="submit"
                    expand="block"
                    className="submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <span className="loading-spinner"></span>
                        Submitting...
                      </span>
                    ) : (
                      'Get Priority Access'
                    )}
                  </IonButton>
                  
                  {isSubmitting && (
                    <div style={{ 
                      textAlign: 'center', 
                      marginTop: '1rem', 
                      color: '#D4AF37',
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}>
                      Please wait while we process your request...
                    </div>
                  )}
                </form>
              </IonCardContent>
            </IonCard>
          </div>
        </motion.section>
        
        {/* App Coming Soon Section */}
        <motion.section
          ref={appComingSoonRef}
          className="app-coming-soon-section"
          initial="hidden"
          animate={appComingSoonControls}
          variants={containerVariants}
        >
          <div className="section-container">
            <motion.div variants={itemVariants} className="app-coming-soon-header">
              <h2 className="app-coming-soon-title">Are You Ready?</h2>
              <p className="app-coming-soon-description">
                Experience KeyToDXB on the go. Your gateway to Dubai luxury, now in your pocket.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="countdown-container">
              <div className="countdown-item">
                <div className="countdown-number">{String(countdown.days).padStart(2, '0')}</div>
                <div className="countdown-label">Days</div>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-item">
                <div className="countdown-number">{String(countdown.hours).padStart(2, '0')}</div>
                <div className="countdown-label">Hours</div>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-item">
                <div className="countdown-number">{String(countdown.minutes).padStart(2, '0')}</div>
                <div className="countdown-label">Minutes</div>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-item">
                <div className="countdown-number">{String(countdown.seconds).padStart(2, '0')}</div>
                <div className="countdown-label">Seconds</div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="app-store-badges">
              <div className="app-store-badge-item">
                <div className="store-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C1.79 15.25 4.6 8.41 9.5 8.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.9c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="store-text">
                  <span className="store-coming-soon">Coming soon to</span>
                  <span className="store-name">App Store</span>
                </div>
              </div>
              
              <div className="app-store-badge-item">
                <div className="store-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27zm-4.81-4.81L3.91 2.66l10.9 6.25-2.81 2.4zm1.12-2.93l2.27 2.27 2.27-2.27c.56-.56 1.08-1.13 1.54-1.72L12.31 2.5l-1.06 1.28z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="store-text">
                  <span className="store-coming-soon">Coming soon to</span>
                  <span className="store-name">Google Play</span>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="app-notify-form-container">
              <p className="app-notify-title">Join the waitlist</p>
              <form onSubmit={handleAppNotifySubmit} className="app-notify-form">
                <IonItem className={`app-notify-input-item ${appNotifyError ? 'form-item-error' : ''}`}>
                  <IonInput
                    type="email"
                    value={appNotifyEmail}
                    onIonInput={(e) => {
                      const value = e.detail.value || '';
                      setAppNotifyEmail(value);
                      if (appNotifyError) {
                        setAppNotifyError(validateEmail(value));
                      }
                    }}
                    placeholder="your.email@example.com"
                    required
                  />
                </IonItem>
                {appNotifyError && <span className="form-error">{appNotifyError}</span>}
                <IonButton
                  type="submit"
                  className="app-notify-btn"
                  disabled={isSubmittingNotify}
                >
                  {isSubmittingNotify ? 'Subscribing...' : 'Get Notified'}
                </IonButton>
              </form>
            </motion.div>
          </div>
        </motion.section>
        
        {/* Footer */}
        <footer className="luxury-footer">
          <div className="footer-container">
            <div className="footer-top">
              <a 
                href="mailto:info@keytodubai.com?subject=Inquiry%20from%20KeyToDXB%20Website" 
                className="footer-email"
                onClick={(e) => {
                  // Ensure mailto works on all devices
                  window.location.href = 'mailto:info@keytodubai.com?subject=Inquiry%20from%20KeyToDXB%20Website';
                }}
              >
                <svg className="footer-email-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                </svg>
                <span>info@keytodubai.com</span>
              </a>
            </div>
            <div className="footer-bottom">
              <p className="footer-copyright">© 2026 KeyToDXB. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.instagram.com/keytodxb" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162 0 3.403 2.759 6.162 6.162 6.162 3.403 0 6.162-2.759 6.162-6.162 0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4 2.209 0 4 1.791 4 4 0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="currentColor"/>
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/keytodxb" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="LinkedIn">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </footer>
        
        <SuccessModal 
          isOpen={showSuccessModal} 
          onClose={() => setShowSuccessModal(false)} 
        />
      </IonContent>
    </IonPage>
  );
};

export default Home;
